import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "../../api";

type Item = { entityType: "program" | "program_state" | "department" | "problem"; entityId: string; name: string; status: string; nextReviewAt: string | null; note: string | null };
const LABEL: Record<string, string> = { needs_review: "Needs review", stale: "Review overdue", pending: "Pending", verified: "Due soon" };

export function VerificationQueue() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["queue"], queryFn: () => api<{ items: Item[] }>("/api/admin/verification/queue") });
  const run = useMutation({
    mutationFn: () => api<{ stale: number; checked: number; broken: number; aborted?: boolean }>("/api/admin/verification/run-checks", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["queue"] }),
  });
  const programIdFor = (i: Item) => i.entityType === "program" ? i.entityId : null;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Verification queue</h1>
        <button className="btn-primary" onClick={() => run.mutate()} disabled={run.isPending}>{run.isPending ? "Checking links…" : "Run link check now"}</button>
      </div>
      {run.data && <p className="mb-3 text-sm">Checked {run.data.checked} links · {run.data.broken} broken · {run.data.stale} records became overdue.{run.data.aborted && " Most links got no response, so this looks like a network problem. No records were changed."}</p>}
      {run.error && <p role="alert" className="mb-3 text-sm text-red-600">{(run.error as Error).message}</p>}
      {q.error && <p role="alert" className="mb-3 text-sm text-red-600">{(q.error as Error).message}</p>}
      {q.data?.items.length === 0 && <p className="text-sm text-slate-500">Nothing needs attention.</p>}
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-slate-500"><tr><th className="py-2">Record</th><th>Type</th><th>Status</th><th>Review by</th><th>Note</th></tr></thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
          {q.data?.items.map((i) => (
            <tr key={i.entityType + i.entityId}>
              <td className="py-2">{programIdFor(i)
                ? <Link to="/admin/programs/$id" params={{ id: i.entityId }} className="text-brand-700 hover:underline">{i.name}</Link>
                : i.entityType === "department" ? <Link to="/admin/departments" search={{ id: i.entityId }} className="text-brand-700 hover:underline">{i.name}</Link>
                : i.entityType === "problem" ? <Link to="/admin/problems" search={{ id: i.entityId }} className="text-brand-700 hover:underline">{i.name}</Link>
                : <span>{i.name}</span>}</td>
              <td>{i.entityType.replace("_", " ")}</td>
              <td>{LABEL[i.status] ?? i.status}</td>
              <td>{i.nextReviewAt ? new Date(i.nextReviewAt).toLocaleDateString() : "—"}</td>
              <td className="max-w-xs truncate text-xs text-slate-500" title={i.note ?? ""}>{i.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
